import React, { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { FaMapMarkedAlt, FaProjectDiagram, FaChevronDown } from 'react-icons/fa'
import EngineeringAtlasTab from './supply-chain/EngineeringAtlasTab'
import SupplyChainAnalytics from './supply-chain/SupplyChainAnalytics'

const API_BASE = process.env.REACT_APP_BE_MAIN_URL
const STORAGE_KEY = 'drawing-intelligence-filters'

// Build a display name from a space's level1..level8 fields
function getSpaceDisplayName(space) {
  const levels = [space.level1, space.level2, space.level3, space.level4,
                  space.level5, space.level6, space.level7, space.level8]
  return levels.filter(Boolean).join(' / ')
}

// Persist filters to localStorage
function saveFilters(selectedSpaceIds, selectedFolderIds) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedSpaceIds, selectedFolderIds }))
  } catch {}
}
function loadFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

// ─── Multi-select dropdown ──────────────────────────────────────────────────
function MultiSelectDropdown({ label, options, selected, onChange, getId, getLabel }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const allSelected = selected.length === options.length
  const noneSelected = selected.length === 0

  const toggleAll = () => {
    onChange(allSelected ? [] : options.map(o => getId(o)))
  }
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id])
  }

  const displayText = allSelected || noneSelected
    ? `All ${label}`
    : selected.length === 1
      ? getLabel(options.find(o => getId(o) === selected[0])) || label
      : `${selected.length} ${label}`

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs rounded-lg px-3 py-1.5 outline-none hover:bg-white/20 transition-colors min-w-[120px]"
      >
        <span className="truncate max-w-[140px]">{displayText}</span>
        <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 min-w-[200px] max-h-[300px] overflow-y-auto">
          <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045]"
            />
            <span className="text-xs font-medium text-gray-700">Select All</span>
          </label>
          {options.map(opt => (
            <label key={getId(opt)} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(getId(opt))}
                onChange={() => toggle(getId(opt))}
                className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045]"
              />
              <span className="text-xs text-gray-700 truncate">{getLabel(opt)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function DrawingIntelligencePage() {
  const history = useHistory()
  const [activeTab, setActiveTab] = useState('atlas')
  const [spaces, setSpaces] = useState([])
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([])
  const [folders, setFolders] = useState([])
  const [selectedFolderIds, setSelectedFolderIds] = useState([])
  const [networkData, setNetworkData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Gate: redirect if supply_chain_v1 is not enabled for this org
  useEffect(() => {
    const token = sessionStorage.getItem("userToken")
    axios.get(`${API_BASE}/user/admin/org/plays/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const enabled = res.data?.enabled_plays || []
      if (!enabled.includes('supply_chain_v1')) {
        history.replace('/OrgChart')
      }
    }).catch(() => {
      // fail-open: render the page if fetch errors
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch spaces on mount
  useEffect(() => {
    const token = sessionStorage.getItem("userToken")
    axios.get(`${API_BASE}/ocr/spaces/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const spaceList = res.data || []
      setSpaces(spaceList)
      // Restore saved selection or select all
      const saved = loadFilters()
      if (saved?.selectedSpaceIds?.length) {
        const valid = saved.selectedSpaceIds.filter(id => spaceList.some(s => String(s.id) === String(id)))
        setSelectedSpaceIds(valid.length ? valid : spaceList.map(s => String(s.id)))
      } else {
        setSelectedSpaceIds(spaceList.map(s => String(s.id)))
      }
    }).catch(() => {})
  }, [])

  // Fetch folders when selected spaces change
  useEffect(() => {
    if (selectedSpaceIds.length === 0) { setFolders([]); setSelectedFolderIds([]); return }
    const token = sessionStorage.getItem("userToken")
    // Fetch folders for all selected spaces
    const requests = selectedSpaceIds.map(spaceId =>
      axios.get(`${API_BASE}/ocr/spaces/${spaceId}/projects/`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => (res.data || []).map(f => ({ ...f, _spaceId: spaceId })))
        .catch(() => [])
    )
    Promise.all(requests).then(results => {
      const allFolders = results.flat()
      setFolders(allFolders)
      // Restore saved folder selection or select all
      if (!initialized) {
        const saved = loadFilters()
        if (saved?.selectedFolderIds?.length) {
          const valid = saved.selectedFolderIds.filter(id => allFolders.some(f => String(f.id) === String(id)))
          setSelectedFolderIds(valid.length ? valid : allFolders.map(f => String(f.id)))
        } else {
          setSelectedFolderIds(allFolders.map(f => String(f.id)))
        }
        setInitialized(true)
      } else {
        // When spaces change after init, select all new folders
        setSelectedFolderIds(allFolders.map(f => String(f.id)))
      }
    })
  }, [selectedSpaceIds]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist selections
  useEffect(() => {
    if (initialized) saveFilters(selectedSpaceIds, selectedFolderIds)
  }, [selectedSpaceIds, selectedFolderIds, initialized])

  // Derive space paths and folder names for API calls
  const selectedSpacePaths = spaces
    .filter(s => selectedSpaceIds.includes(String(s.id)))
    .map(s => getSpaceDisplayName(s))
  const selectedFolderNames = folders
    .filter(f => selectedFolderIds.includes(String(f.id)))
    .map(f => f.folder_name)

  // Fetch network data when selection changes
  const fetchNetworkData = useCallback(() => {
    if (selectedSpacePaths.length === 0 || selectedFolderNames.length === 0) return
    setLoading(true)
    const token = sessionStorage.getItem("userToken")
    axios.get(`${API_BASE}/ocr/network/summary/`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        space_path: selectedSpacePaths.join(','),
        folder_name: selectedFolderNames.join(','),
      }
    }).then(res => {
      setNetworkData(res.data)
    }).catch(() => {
      setNetworkData(null)
    }).finally(() => {
      setLoading(false)
    })
  }, [selectedSpacePaths.join(','), selectedFolderNames.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchNetworkData()
  }, [fetchNetworkData])

  // Reset all filters to "all selected"
  const resetFilters = () => {
    setSelectedSpaceIds(spaces.map(s => String(s.id)))
    setSelectedFolderIds(folders.map(f => String(f.id)))
  }

  const spacePath = selectedSpacePaths.join(',')
  const folderName = selectedFolderNames.join(',')

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e5045] px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/10">
              <FaMapMarkedAlt className="text-white w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Drawing Intelligence</h1>
              <p className="text-white/60 text-xs">Engineering atlas & supply chain analytics</p>
            </div>
          </div>

          {/* Filter section */}
          <div className="flex items-center gap-3">
            <MultiSelectDropdown
              label="Projects"
              options={spaces}
              selected={selectedSpaceIds}
              onChange={setSelectedSpaceIds}
              getId={s => String(s.id)}
              getLabel={s => getSpaceDisplayName(s)}
            />
            <MultiSelectDropdown
              label="Folders"
              options={folders}
              selected={selectedFolderIds}
              onChange={setSelectedFolderIds}
              getId={f => String(f.id)}
              getLabel={f => f.folder_name}
            />
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="max-w-[1600px] mx-auto flex gap-0">
          <button
            onClick={() => setActiveTab('atlas')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'atlas'
                ? 'text-[#1e5045] border-[#1e5045] font-semibold'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <FaMapMarkedAlt size={14} />
            Engineering Atlas
          </button>
          <button
            onClick={() => setActiveTab('supply-chain')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'supply-chain'
                ? 'text-[#1e5045] border-[#1e5045] font-semibold'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <FaProjectDiagram size={14} />
            Supply Chain Analytics
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-5">
        {activeTab === 'atlas' && (
          <EngineeringAtlasTab
            spacePath={spacePath}
            folderName={folderName}
            onResetAll={resetFilters}
          />
        )}
        {activeTab === 'supply-chain' && (
          <SupplyChainAnalytics
            spacePath={spacePath}
            folderName={folderName}
            networkData={networkData}
            loading={loading}
            onRefresh={fetchNetworkData}
          />
        )}
      </div>
    </div>
  )
}
